import { ArrowHandle } from '@pcln/fly-ui-components'
import { Box, Relative } from 'pcln-design-system'
import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  StyledPushPin,
  StyledPushPinBorder,
  DynamicFiltersWrapper,
  StyledFilterChip,
  StyledPinChip,
} from './DynamicFilters.styles'
import { getSelected } from './helpers'
import { useChangeHandlers, useDynamicFiltersData, useStateProps } from '../Hooks'
import { SortOptions } from '../SortOptions'
import { isDesktopVP } from '../../../util/displayHelpers'
import { useDebouncedListingsFilterCall } from '@mfs/client/listings-client/components/Filters/useDebouncedListingsFilterCall/useDebouncedListingsFilterCall'
import { MobileSponsoredFilterChip } from './MobileSponsoredFilterChip'

/**
 * Scrollable Dynamic Filters Component on top of Listings
 *
 * @param {action} listingsFilterCall - for performing redux filter call
 * @returns
 */
function DynamicFilters({ pinCount = 0, handlePinSelect = () => {}, isPinSelected, isPinVariant = false }) {
  /**
   * Grab necessary props from state (e.g. current airport filter checkbox status, bootstrap data, etc. )
   */
  const stateProps = useStateProps()
  const { airportsFilter, listingsIsLoading, searchRequest, pclnBootstrapData, activeTabId } = stateProps

  /**
   * Custom hook to perform API call
   */
  const { chips } = useDynamicFiltersData({ searchRequest, pclnBootstrapData, activeTabId })

  /**
   * Current selected state of chips
   */
  const [selected, setSelected] = useState({})

  /**
   * Obtain the click + change handlers on filter click/select/unselect purposes
   */
  const { handleClick } = useChangeHandlers({
    ...stateProps,
    selected,
    setSelected,
  })

  const { debouncedListingsFilterCall } = useDebouncedListingsFilterCall()

  const isMobile = !isDesktopVP()
  const isNotReshopAndWLAndPackages = useSelector(
    (state) => state?.pclnBootstrapData?.isNotReshopAndWLAndPackages
  )

  let pinChipProps = {
    Icon: isPinSelected ? StyledPushPin : StyledPushPinBorder,
  }

  const handleSortOptionChange = (sortOptions) =>
    debouncedListingsFilterCall(pclnBootstrapData, 'filter', sortOptions)

  return (
    !!chips &&
    chips?.length > 0 &&
    !listingsIsLoading &&
    airportsFilter?.length > 0 && (
      <Box
        color='background.lightest'
        m='auto'
        maxWidth='1280px'
        pl={[1, null, null, 0]}
        data-testid='DYNAMIC_FILTERS_CONTAINER'
        aria-label='DYNAMIC_FILTERS_CONTAINER'
      >
        <Relative width={1}>
          <DynamicFiltersWrapper
            alignItems={['flex-start', null, null, 'center']}
            flexDirection={['row', null, null, 'row']}
            mr={[2, null, null, 0]}
            aria-label='DYNAMIC_FILTERS_WRAPPER'
          >
            <ArrowHandle
              gaCallback={() => {}}
              allowScrolling
              aria-label='DYNAMIC_FILTERS_ARROW_COMPONENT'
              data-testid='DYNAMIC_FILTERS_ARROW_COMPONENT'
            >
              {isMobile && (
                <Box>
                  <SortOptions
                    changeHandler={handleSortOptionChange}
                    numOfSlices={pclnBootstrapData.searchRequest?.slices?.length}
                    isDynamic
                    disabled={isMobile && isPinSelected && pinCount > 0}
                  />
                </Box>
              )}
              {isMobile && isNotReshopAndWLAndPackages && (
                <MobileSponsoredFilterChip pclnBootstrapData={pclnBootstrapData} />
              )}
              {isPinVariant && pinCount > 0 && (
                <StyledPinChip
                  title={'Pinned count'}
                  label={`Your Pinned Flights (${pinCount})`}
                  align={'center'}
                  key={'PINNED_ITINS'}
                  px={2}
                  mx={6}
                  data-test-id={`PINNED_ITINS_FILTER_CHIP`}
                  aria-label={`PINNED_ITINS_FILTER_CHIP`}
                  selected={isPinSelected && pinCount > 0}
                  onClick={handlePinSelect}
                  {...pinChipProps}
                />
              )}
              {chips?.map((data) => {
                const noTranslate = data?.type === 'AIRLINES'
                /**
                 * Obtain necessary props for chips
                 */
                const { type, filterId, display } = data
                const chipProps = {
                  ...stateProps,
                  type,
                  filterId,
                }

                /**
                 * Get selected/highlighted state of chip
                 */
                const isSelectedFromState = getSelected(chipProps)
                /**
                 * Only render chip if display text is non-null (i.e. chip exists)
                 */
                return (
                  !!display && (
                    <StyledFilterChip
                      title={type}
                      label={display}
                      align={'center'}
                      key={type + '_' + filterId}
                      px={2}
                      mx={6}
                      data-test-id={`DYNAMIC_FILTER_CHIP_${type}`}
                      aria-label={`DYNAMIC_FILTER_CHIP_${type}`}
                      selected={isSelectedFromState}
                      showActionIcon={isSelectedFromState}
                      translate={noTranslate ? 'no' : ''}
                      onClick={() => {
                        handleClick({
                          type,
                          filterId,
                          isSelectedFromState,
                        })
                      }}
                      disabled={isPinSelected && pinCount > 0}
                    />
                  )
                )
              })}
            </ArrowHandle>
          </DynamicFiltersWrapper>
        </Relative>
      </Box>
    )
  )
}

export default memo(DynamicFilters)
